import React, { useState } from 'react'
import {
  ProtectMode,
  ProtectModeBadge,
  ProtectModeDropdown
} from '../../protect-mode'
import { css } from '@emotion/css'
import { ServerEnvironment } from '../../server-environment'
import { useUpdateCveShield } from '../use-update-cve-shield'
import { useQueryClient } from 'react-query'
import { Query } from '../../../hooks/queries'
import { logError } from '../../toaster'

interface ProtectModeCellProps {
  readonly rowData: {
    readonly uuid: string
    readonly protectMode: ProtectMode
  }
  readonly organizationUuid: string
  readonly environment: ServerEnvironment
  readonly disabled: boolean
}

export function ProtectModeCell({
  rowData: { protectMode, uuid: ruleUuid },
  organizationUuid,
  environment,
  disabled
}: ProtectModeCellProps) {
  const queryClient = useQueryClient()
  const [selectedMode, setSelectedMode] = useState<ProtectMode>(protectMode)

  const { mutate } = useUpdateCveShield({
    // Optimistically update the cache value on mutate, but store
    // the old value and return it so that it's accessible in case of
    // an error
    onMutate: ({ mode }) => {
      const previousProtectMode = protectMode
      setSelectedMode(mode)
      return { previousProtectMode }
    },
    // Handle any errors, and after success or failure, refetch the query
    onSettled: (_data, error, _variables, { previousProtectMode }) => {
      if (error) {
        setSelectedMode(previousProtectMode)
        logError(error.message)
      }
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      queryClient.refetchQueries([Query.CVE_SHIELDS_SUMMARY], {
        active: true,
        exact: true
      })
    }
  })

  return (
    <div
      className={css`
        display: inline-block;
      `}
    >
      {disabled ? (
        <ProtectModeBadge mode={selectedMode} showDropdownStyle={false} />
      ) : (
        <ProtectModeDropdown
          modes={['OFF', 'MONITORING', 'BLOCKING']}
          selectedMode={selectedMode}
          onChange={(mode) => {
            mutate({
              organizationUuid,
              mode,
              environment,
              ruleUuid
            })
          }}
        />
      )}
    </div>
  )
}
